import {
  collection, doc, getDocs, limit, orderBy, query, runTransaction,
  serverTimestamp, startAfter, where, type DocumentData, type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import type { FirestoreDate } from '../types';

// Leave ample room below Firestore's document limit, including UTF-8 text.
export const MAX_PHONE_CALL_TRANSCRIPT_LENGTH = 200_000;
export type PhoneCallPerson = {
  type: 'partner_contact' | 'parent';
  id: string;
  displayName: string;
};
export type PartnerPhoneCall = {
  id: string;
  partnerId: string;
  personType: PhoneCallPerson['type'];
  personId: string;
  personNameSnapshot: string;
  transcript: string;
  callDate: string | null;
  source: 'pasted_transcript';
  createdBy: string;
  createdAt: FirestoreDate;
};
export type PhoneCallPage = {
  calls: PartnerPhoneCall[];
  cursor: QueryDocumentSnapshot<DocumentData> | null;
};

function callsCollection(partnerId: string) {
  return collection(db, 'partners', partnerId, 'phoneCalls');
}

export function newPhoneCallId(partnerId: string): string {
  return doc(callsCollection(partnerId)).id;
}

export async function savePartnerPhoneCall(params: {
  id: string;
  partnerId: string;
  person: PhoneCallPerson;
  transcript: string;
  callDate: string;
  actorId: string;
}): Promise<void> {
  const { id, partnerId, person, transcript, callDate, actorId } = params;
  if (!actorId || !partnerId || !person.id || !id) throw new Error('Sign in and select a contact before saving.');
  if (!transcript.trim()) throw new Error('Paste a phone call transcript before saving.');
  if (transcript.length > MAX_PHONE_CALL_TRANSCRIPT_LENGTH) throw new Error('This transcript exceeds the 200,000 character limit. It has not been saved or shortened.');
  if (callDate && (!/^\d{4}-\d{2}-\d{2}$/.test(callDate) || Number.isNaN(Date.parse(callDate)) || new Date(callDate).toISOString().slice(0, 10) !== callDate)) {
    throw new Error('Enter a valid call date or leave it blank.');
  }
  const ref = doc(callsCollection(partnerId), id);
  // Reuse the same ID after an uncertain save so a retry cannot duplicate a call.
  await runTransaction(db, async (transaction) => {
    const existing = await transaction.get(ref);
    if (existing.exists()) {
      const saved = existing.data();
      if (saved.personId === person.id && saved.personType === person.type && saved.transcript === transcript && saved.callDate === (callDate || null) && saved.createdBy === actorId) return;
      throw new Error('This call was already saved. Reopen the call history before adding another call.');
    }
    transaction.set(ref, {
      partnerId,
      personType: person.type,
      personId: person.id,
      personNameSnapshot: person.displayName,
      transcript, // Preserve the original exactly, including whitespace and speaker labels.
      callDate: callDate || null,
      source: 'pasted_transcript',
      createdBy: actorId,
      createdAt: serverTimestamp(),
    });
  });
}

export async function listPartnerPhoneCalls(
  partnerId: string,
  person: PhoneCallPerson,
  cursor?: QueryDocumentSnapshot<DocumentData> | null,
): Promise<PhoneCallPage> {
  const snapshot = await getDocs(query(
    callsCollection(partnerId),
    where('personType', '==', person.type),
    where('personId', '==', person.id),
    orderBy('createdAt', 'desc'),
    ...(cursor ? [startAfter(cursor)] : []),
    limit(20),
  ));
  return {
    calls: snapshot.docs.map((item) => ({ ...item.data(), id: item.id } as PartnerPhoneCall)),
    cursor: snapshot.size === 20 ? snapshot.docs[snapshot.docs.length - 1] : null,
  };
}
